<template>
    <!--
        Hover is handled once here rather than per cell. The allocations layer and the create-button layer
        below both cover the day cells and swallow the pointer, so a per-cell listener only fires in the
        slivers they leave free. Mouse events bubble, so this fires whichever layer is actually under the
        pointer, and the column comes from the pointer's x position.

        Both events are needed: `mousemove` tracks the column while the pointer moves (including across a
        single allocation part, whose backgrounds can be wider than one column, so `mouseover` alone would
        not re-fire), and `mouseover` covers the row arriving under a stationary pointer, e.g. when the
        timeline is scrolled with the wheel. The change-guard in the handler makes the overlap free.
    -->
    <div
        class="flex flex-row gap-px bg-gray-200 relative"
        :style="{ minHeight: `${heightInRem}rem` }"
        @mouseover="onRowPointerMove"
        @mousemove="onRowPointerMove"
    >
        <StaticCell
            v-for="day in days"
            :key="`static-cell-${day.index}`"
            :is-in-hovered-column="`column-${day.index}` === hoveredTimelineParts.columnId.value"
            :is-in-hovered-row="props.rowId === hoveredTimelineParts.rowId.value"
            :is-conflict-hovered="hoveredTimelineParts.isConflictCell.value"
            :is-work-day="day.isWorkDay"
            :conflict-marker="getConflictMarkerForDay(day.isoDate)"
        />

        <!-- The allocations are rendered in this slot -->
        <div class="absolute size-full pointer-events-none z-3 mt-px">
            <slot />
        </div>

        <!--
            This spans the entire height. Per day, a spacer reserves the slots taken by that day's allocations
            and the create button fills everything below it, so it covers all of the column's free slots.
        -->
        <div v-if="isCreateButtonVisible" class="absolute size-full flex flex-row gap-px">
            <div
                v-for="day in days"
                :key="`create-button-${day.index}`"
                class="flex flex-col"
                :class="columnWidthClass"
            >
                <div :style="getSpacerHeightStyle(day.index)" class="shrink-0" />

                <FullHeightCreateButton
                    class="grow relative z-2 hover:z-30 focus-within:z-30"
                    :status="getButtonStatusForDay(day.index)"
                    :tooltip-header="getTooltipHeaderForDay(day.dateTime)"
                    :is-in-hovered-column="`column-${day.index}` === hoveredTimelineParts.columnId.value"
                    :is-in-hovered-row="props.rowId === hoveredTimelineParts.rowId.value"
                    @click="
                        emit('create-allocation', {
                            startDateTime: timelineRange.startDateTime.value.plus({ days: day.index }),
                            endDateTime: timelineRange.startDateTime.value.plus({ days: day.index + 1 }),
                        })
                    "
                />
            </div>
        </div>

        <div class="absolute inset-0 grid gap-x-px pointer-events-none" :style="gridTemplateColumns">
            <div
                v-for="day in monthStartDays"
                :key="day.index"
                class="w-0.5 ml-[-0.09375rem] bg-gray-300"
                :style="{ gridColumnStart: day.index + 1 }"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { filterVNodesByComponent, flattenVNodes } from '@/helpers/vnodeHelpers';
import { $t } from '@/plugins/fluent';
import { formatDate } from '@/utils/date';
import { stripBidiTokens } from '@/utils/text';
import { DateTime } from 'luxon';
import { computed, inject, provide, ref, useSlots, watchEffect } from 'vue';
import type { AllocationProps } from '../allocation/Allocation.vue';
import Allocation from '../allocation/Allocation.vue';
import { useHeightSync } from '../composables/useHeightSync.ts';
import type { LayoutAlgorithm } from '../composables/useLayoutAllocations.ts';
import { calculateCreateButtonSpacerPerDay, useLayoutAllocations } from '../composables/useLayoutAllocations.ts';
import {
    hoveredTimelinePartsKey,
    hoverTimelinePartKey,
    positionInLayoutRowKey,
    timelineRangeKey,
} from '../injectionKeys';
import { columnIndexAtPointer } from '../logic/hover';
import {
    allocationBorderWidthInRem,
    allocationBoxHeightInRem,
    columnWidthClass,
    columnWidthInRem,
} from '../sharedStyles';
import { ConflictMarker } from '../Timeline.types';
import type { FullHeightCreateButtonStatus } from './FullHeightCreateButton.vue';
import FullHeightCreateButton from './FullHeightCreateButton.vue';
import StaticCell from './StaticCell.vue';

export type CreateButtonStatus =
    | { type: 'Visible'; rowLabel: string; perDayStatus: Map<string, FullHeightCreateButtonStatus> }
    | { type: 'Disabled'; rowLabel: string; tooltipText: string }
    | { type: 'Hidden' };

type LayoutRowProps = {
    /** Shared row identity: pairs this row with its first-column cell for hover and height sync. */
    rowId: string;
    isFirstRow: boolean;
    layoutAlgorithm: LayoutAlgorithm;
    createButtonStatus: CreateButtonStatus;
    checkboxGroup?: string;
    /**
     * Map from ISO date string (YYYY-MM-DD) to conflict marker.
     * If a day is not in the map, it has no conflict.
     */
    conflictMarkers?: Map<string, ConflictMarker>;
};

const props = defineProps<LayoutRowProps>();

const emit = defineEmits<{
    (e: 'create-allocation', payload: { startDateTime: DateTime; endDateTime: DateTime }): void;
}>();

const timelineRange = inject(timelineRangeKey);
if (!timelineRange) {
    throw new Error('LayoutRow must be used within a Timeline component');
}

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
const hoverTimelinePart = inject(hoverTimelinePartKey);

if (!hoveredTimelineParts || !hoverTimelinePart) {
    throw new Error('LayoutRow must be used within a Timeline component');
}

const isCreateButtonVisible = computed(
    () => props.createButtonStatus.type === 'Visible' || props.createButtonStatus.type === 'Disabled',
);

type RequiredAllocationProps = Pick<AllocationProps, 'id' | 'startDateTime' | 'endDateTime'>;
const registeredAllocations = ref<
    Record<
        string,
        RequiredAllocationProps & { left: number | null; top: number | null; allocationVariantIndex: number }
    >
>({});

const { layout } = useLayoutAllocations(props.layoutAlgorithm);

const { setHeight, heightInRem } = useHeightSync(props.rowId, props.isFirstRow ? 1.3125 : 1.25);

const daysCount = computed(() => timelineRange.endDateTime.value.diff(timelineRange.startDateTime.value, 'days').days);
const days = computed(() => {
    const daysArray: { index: number; isWorkDay: boolean; isoDate: string; dateTime: DateTime }[] = [];
    for (let delta = 0; delta < daysCount.value; delta++) {
        const dateTime = timelineRange.startDateTime.value.plus({ days: delta });
        const isoDate = dateTime.toISODate();
        if (!isoDate) {
            throw new Error('Could not convert DateTime to ISO date string');
        }
        daysArray.push({
            index: delta,
            isWorkDay: dateTime.weekday !== 6 && dateTime.weekday !== 7,
            isoDate,
            dateTime,
        });
    }
    return daysArray;
});

// Like the header, the first column gets a marker even if the range starts mid-month
const monthStartDays = computed(() => days.value.filter((day) => day.index === 0 || day.dateTime.day === 1));
const gridTemplateColumns = computed(() => `grid-template-columns: repeat(${daysCount.value}, ${columnWidthInRem}rem)`);

// Per-day spacer height (in rem) above the create buttons, from the allocations occupying each day
const createButtonSpacerPerDay = ref<Map<number, number>>(new Map());

const slots = useSlots();
watchEffect(() => {
    const flattenedVNodes = flattenVNodes(slots.default?.());
    const allocations = filterVNodesByComponent<AllocationProps>(flattenedVNodes, Allocation);

    // Register new allocations
    const newRegisteredAllocations: typeof registeredAllocations.value = {};

    allocations.forEach((allocation) => {
        if (newRegisteredAllocations[allocation.props.id]) {
            throw new Error(`Allocation ${allocation.props.id} already registered in LayoutRow`);
        }

        newRegisteredAllocations[allocation.props.id] = {
            id: allocation.props.id,
            startDateTime: allocation.props.startDateTime,
            endDateTime: allocation.props.endDateTime,
            allocationVariantIndex: allocation.props.allocationVariant.index,
            left: null,
            top: null,
        };
    });

    // Use the layout algorithm to calculate positions and row height
    const allocationsData = Object.values(newRegisteredAllocations).map((a) => ({
        id: a.id,
        startDateTime: a.startDateTime,
        endDateTime: a.endDateTime,
        allocationVariantIndex: a.allocationVariantIndex,
    }));

    const { positionedAllocations, rowHeight: calculatedRowHeight } = layout(
        allocationsData,
        timelineRange.startDateTime.value,
        props.isFirstRow,
    );

    // Update allocations with calculated positions
    Object.keys(newRegisteredAllocations).forEach((allocationId) => {
        const position = positionedAllocations[allocationId];
        if (position) {
            newRegisteredAllocations[allocationId] = {
                ...newRegisteredAllocations[allocationId],
                left: position.left,
                top: position.top,
            };
        }
    });

    // When create buttons are shown, reserve one extra allocation slot at the bottom for them
    if (props.createButtonStatus.type !== 'Hidden') {
        createButtonSpacerPerDay.value = calculateCreateButtonSpacerPerDay(
            Object.values(newRegisteredAllocations),
            timelineRange.startDateTime.value,
            daysCount.value,
            props.isFirstRow,
        );

        // Add one allocation slot worth of height for the create button, minus its 1px border overlap
        setHeight(calculatedRowHeight + allocationBoxHeightInRem - allocationBorderWidthInRem);
    } else {
        createButtonSpacerPerDay.value = new Map();
        setHeight(calculatedRowHeight);
    }

    // Update the ref in one go
    registeredAllocations.value = newRegisteredAllocations;
});
provide(positionInLayoutRowKey, (allocationId) => {
    const allocation = registeredAllocations.value[allocationId];
    if (!allocation) {
        throw new Error('Allocation not registered in LayoutRow');
    }

    return { left: allocation.left, top: allocation.top };
});

/**
 * The fallback is the row's top padding, i.e. the "no allocation on this day" spacer, which can only be hit
 * if the layout effect has not run for a day index yet. It leaves the button covering the whole cell rather
 * than collapsing it to nothing.
 */
function getSpacerHeightStyle(dayIndex: number) {
    const spacerHeightInRem =
        createButtonSpacerPerDay.value.get(dayIndex) ?? (props.isFirstRow ? allocationBorderWidthInRem : 0);

    return { height: `${spacerHeightInRem}rem` };
}

function getConflictMarkerForDay(isoDate: string): ConflictMarker {
    return props.conflictMarkers?.get(isoDate) ?? 'No-Conflict';
}

function hasConflictOnDay(isoDate: string): boolean {
    const marker = props.conflictMarkers?.get(isoDate);
    return marker !== undefined && marker !== 'No-Conflict';
}

// Last column reported to the store, so ordinary movement within one column writes nothing. Besides
// saving the reactive cascade (every cell, create button and header day re-reads the hover state), this
// keeps the DOM untouched between events so the rect read below stays a cached-layout read.
let lastReportedColumnId: string | null = null;

// An arrow const rather than a `function`: TypeScript keeps the narrowing of the injected `hoverTimelinePart`
// / `hoveredTimelineParts` consts inside a closure created after their guard, but not inside a hoisted
// function declaration — which would otherwise force a redundant re-guard in the body.
const onRowPointerMove = (event: MouseEvent) => {
    const row = event.currentTarget;
    if (!(row instanceof HTMLElement)) {
        return;
    }

    const rect = row.getBoundingClientRect();
    const columnIndex = columnIndexAtPointer(event.clientX, rect.left, rect.width, days.value.length);
    if (columnIndex === null) {
        return;
    }

    const columnId = `column-${days.value[columnIndex].index}`;
    if (columnId === lastReportedColumnId && props.rowId === hoveredTimelineParts.rowId.value) {
        return;
    }
    lastReportedColumnId = columnId;

    hoverTimelinePart(props.rowId, columnId, hasConflictOnDay(days.value[columnIndex].isoDate));
};

function getButtonStatusForDay(dayIndex: number): FullHeightCreateButtonStatus {
    if (props.createButtonStatus.type === 'Disabled') {
        return { type: 'Disabled', tooltipText: props.createButtonStatus.tooltipText };
    }

    if (props.createButtonStatus.type === 'Visible') {
        if (!timelineRange) {
            throw new Error('Unexpected error: timelineRange not available in LayoutRow. Should never happen.');
        }
        const isoKey = timelineRange.startDateTime.value.plus({ days: dayIndex }).toISO();

        if (!isoKey) {
            throw new Error('Conversion toISO() failed for startDateTime');
        }

        return props.createButtonStatus.perDayStatus.get(isoKey) ?? { type: 'Create' };
    }

    // Hidden — unreachable due to v-if guard, but satisfies the type system
    return { type: 'Create' };
}

function getTooltipHeaderForDay(dateTime: DateTime): string {
    if (props.createButtonStatus.type === 'Hidden') {
        throw new Error('getTooltipHeaderForDay called when create button is hidden');
    }

    return stripBidiTokens(
        $t('create-allocation-tooltip-header', {
            row: props.createButtonStatus.rowLabel,
            date: formatDate(dateTime, 'dd.MM.yyyy'),
        }),
    );
}
</script>
